// Iter50 — Engineering progress / result form.
//
// Lets the engineering provider record:
//   - corrective_action (required)
//   - progress_pct (0..100, slider + chips)
//   - evidence_attachments[] (photos, when SR is incident-linked)
//   - completion_report (single PDF, when SR is incident-linked)
//   - notes (optional)
//
// Submit flow:
//   1. Upload evidence + report (if incident-linked) to
//      POST /api/incidents/{incidentId}/attachments?source=engineering_result:<sr_id>
//   2. PATCH /api/service-requests/{sr_id}/engineering-result with the full payload
//
// Standalone SRs (no incident_id) skip the attachment step gracefully.
import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHOTO_EXT = /\.(jpe?g|png|gif|webp|heic|heif)$/i;
const PDF_EXT = /\.pdf$/i;
const MAX_PHOTOS = 8;
const QUICK_PCTS = [10, 25, 50, 75, 90, 100];

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color,
  fontWeight: weight,
  letterSpacing: ls,
});

const progressColor = (p) => (p >= 100 ? "#4ed084" : p >= 75 ? "#9ec3ec" : p >= 25 ? "#f3b14a" : "#9aa1b3");

export const EngineeringResultForm = ({ sr, onSubmitted, onCancel }) => {
  const addToast = useStore((s) => s.addToast);
  const existing = sr?.engineering_result || {};

  const [correctiveAction, setCorrectiveAction] = useState(existing.corrective_action || "");
  const [progress, setProgress] = useState(existing.progress_pct ?? 0);
  const [notes, setNotes] = useState(existing.notes || "");
  const [photos, setPhotos] = useState([]);
  const [reportPdf, setReportPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const photoRef = useRef(null);
  const pdfRef = useRef(null);

  const incidentId = sr?.incident_id || null;
  const canAttach = Boolean(incidentId);

  const existingEvidence = useMemo(
    () => (existing.evidence_attachments || []).slice(),
    [existing.evidence_attachments],
  );

  const onPhotoPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const valid = picked.filter((f) => PHOTO_EXT.test(f.name));
    if (valid.length < picked.length) {
      setError("Some files were skipped — only image formats are accepted.");
    }
    setPhotos((prev) => {
      const merged = [...prev];
      for (const f of valid) {
        if (merged.length >= MAX_PHOTOS) break;
        if (!merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
      }
      return merged;
    });
    if (photoRef.current) photoRef.current.value = "";
  };
  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const onPdfPick = (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (!PDF_EXT.test(f.name)) {
      setError("Completion report must be a .pdf file.");
      if (pdfRef.current) pdfRef.current.value = "";
      return;
    }
    setReportPdf(f);
    if (pdfRef.current) pdfRef.current.value = "";
  };

  const uploadAttachments = async () => {
    if (!canAttach) return { photoIds: [], pdfId: null };
    const photoIds = [];
    let pdfId = null;
    if (photos.length) {
      const fd = new FormData();
      photos.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(
        `${API}/incidents/${incidentId}/attachments?source=engineering_result:${encodeURIComponent(sr.id)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      (data.saved || []).forEach((a) => photoIds.push(a.id));
    }
    if (reportPdf) {
      const fd = new FormData();
      fd.append("files", reportPdf);
      const { data } = await axios.post(
        `${API}/incidents/${incidentId}/attachments?source=engineering_result:${encodeURIComponent(sr.id)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      pdfId = (data.saved?.[0]?.id) || null;
    }
    return { photoIds, pdfId };
  };

  const submit = async () => {
    setError("");
    if (!correctiveAction.trim()) {
      setError("Corrective action is required.");
      return;
    }
    setBusy(true);
    try {
      let evidence = [...existingEvidence];
      let report = existing.completion_report || null;
      if (canAttach && (photos.length || reportPdf)) {
        const { photoIds, pdfId } = await uploadAttachments();
        evidence = [...evidence, ...photoIds];
        if (pdfId) report = pdfId;
      }
      const payload = {
        corrective_action: correctiveAction.trim(),
        progress_pct: parseInt(progress, 10) || 0,
        evidence_attachments: evidence,
        completion_report: report,
        notes: notes.trim() || null,
      };
      const { data: updated } = await axios.patch(
        `${API}/service-requests/${sr.id}/engineering-result`,
        payload,
      );
      addToast({
        type: "success",
        title: "Engineering result saved",
        body: `${sr.id} · ${payload.progress_pct}%`,
      });
      onSubmitted?.(updated);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Save failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md p-4"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
      data-testid="engineering-result-form"
    >
      <div className="mb-3 flex items-baseline gap-2" style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
        Engineering progress
        <span style={mono(10, "#9198b0")} data-testid="engineering-form-sr">{sr?.id}</span>
        {sr?.incident_id && (
          <span style={mono(10, "#9ec3ec")}>· {sr.incident_id}</span>
        )}
      </div>

      <div className="mb-4">
        <Label>Corrective action *</Label>
        <textarea
          value={correctiveAction}
          onChange={(e) => setCorrectiveAction(e.target.value)}
          rows={4}
          placeholder="Describe the engineering action taken: investigation findings, parameter changes, redesign steps, validation..."
          className="w-full px-2 py-1.5 rounded outline-none"
          style={inputStyle(12, "vertical")}
          data-testid="engineering-form-corrective-action"
        />
      </div>

      <div className="mb-4">
        <Label>Progress — {progress}%</Label>
        <div className="flex items-center gap-3" data-testid="engineering-form-progress-row">
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={progress}
            onChange={(e) => setProgress(parseInt(e.target.value, 10))}
            style={{ flex: 1, accentColor: progressColor(progress) }}
            data-testid="engineering-form-progress-slider"
          />
          <input
            type="number"
            min="0"
            max="100"
            value={progress}
            onChange={(e) => {
              const v = Math.max(0, Math.min(100, parseInt(e.target.value || 0, 10)));
              setProgress(v);
            }}
            className="px-2 py-1 rounded outline-none"
            style={{ ...inputStyle(12), width: 72, textAlign: "right", color: progressColor(progress), fontWeight: 700 }}
            data-testid="engineering-form-progress-input"
          />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {QUICK_PCTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setProgress(p)}
              className="px-2 py-0.5 rounded"
              style={{
                background: progress === p ? "rgba(24,95,165,0.18)" : "transparent",
                border: `1px solid ${progress === p ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                color: progress === p ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
                ...mono(10, undefined, 700, "0.4px"),
                cursor: "pointer",
              }}
              data-testid={`engineering-form-progress-quick-${p}`}
            >{p}%</button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <Label>Notes</Label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Optional notes — blocker, next step, follow-up owner…"
          className="w-full px-2 py-1.5 rounded outline-none"
          style={inputStyle(11, "vertical")}
          data-testid="engineering-form-notes"
        />
      </div>

      {/* Attachments — only when incident-linked */}
      {canAttach ? (
        <div className="grid grid-cols-2 gap-3 mb-3" data-testid="engineering-form-attachments">
          <div>
            <input ref={photoRef} type="file" accept="image/*,.heic,.heif" multiple onChange={onPhotoPick} style={{ display: "none" }} data-testid="engineering-form-photo-input" />
            <button
              type="button" onClick={() => photoRef.current?.click()} disabled={photos.length >= MAX_PHOTOS}
              className="w-full px-2 py-2 rounded"
              style={{ background: "rgba(24,95,165,0.08)", border: "1px dashed rgba(24,95,165,0.35)", color: photos.length >= MAX_PHOTOS ? "#6b7388" : "#9ec3ec", ...mono(10), cursor: photos.length >= MAX_PHOTOS ? "not-allowed" : "pointer" }}
              data-testid="engineering-form-photo-btn"
            >
              📷 Add evidence ({photos.length}/{MAX_PHOTOS})
            </button>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {photos.map((f, i) => (
                  <div key={`${f.name}-${i}`} style={{ background: "rgba(78,208,132,0.10)", border: "1px solid rgba(78,208,132,0.30)", ...mono(9, "#9aebc2"), padding: "1px 6px", borderRadius: 3, display: "flex", alignItems: "center", gap: 3 }} data-testid={`engineering-form-photo-chip-${i}`}>
                    <span style={{ maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <button type="button" onClick={() => removePhoto(i)} style={{ color: "#ff9a99", fontSize: 11, cursor: "pointer", padding: "0 2px", background: "transparent", border: "none" }} data-testid={`engineering-form-photo-remove-${i}`}>×</button>
                  </div>
                ))}
              </div>
            )}
            {existingEvidence.length > 0 && (
              <div className="mt-1.5" style={mono(9, "#9198b0")} data-testid="engineering-form-existing-evidence">
                {existingEvidence.length} existing evidence file(s) preserved
              </div>
            )}
          </div>
          <div>
            <input ref={pdfRef} type="file" accept=".pdf,application/pdf" onChange={onPdfPick} style={{ display: "none" }} data-testid="engineering-form-pdf-input" />
            <button
              type="button" onClick={() => pdfRef.current?.click()}
              className="w-full px-2 py-2 rounded"
              style={{ background: "rgba(155,89,182,0.08)", border: "1px dashed rgba(155,89,182,0.35)", color: "#c89adb", ...mono(10), cursor: "pointer" }}
              data-testid="engineering-form-pdf-btn"
            >
              📄 {reportPdf ? "Replace completion report" : (existing.completion_report ? "Replace completion report" : "Attach completion report")}
            </button>
            {reportPdf && (
              <div className="mt-1.5 flex items-center justify-between px-2 py-1 rounded" style={{ background: "rgba(155,89,182,0.10)", border: "1px solid rgba(155,89,182,0.30)", ...mono(9, "#c89adb") }} data-testid="engineering-form-pdf-chip">
                <span style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{reportPdf.name}</span>
                <button type="button" onClick={() => setReportPdf(null)} style={{ color: "#ff9a99", fontSize: 12, cursor: "pointer", padding: "0 2px", background: "transparent", border: "none" }} data-testid="engineering-form-pdf-remove">×</button>
              </div>
            )}
            {!reportPdf && existing.completion_report && (
              <div className="mt-1.5" style={mono(9, "#9198b0")} data-testid="engineering-form-existing-report">
                Existing completion report preserved
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-3 px-3 py-2 rounded"
          style={{ background: "rgba(155,89,182,0.06)", border: "1px dashed rgba(155,89,182,0.35)", ...mono(10, "#c89adb") }}
          data-testid="engineering-form-attachments-disabled">
          Standalone request — attachments will be available once linked to an incident.
        </div>
      )}

      {error && (
        <div className="p-2 rounded mb-2" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", ...mono(11, "#ff9a99") }} data-testid="engineering-form-error">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button" onClick={onCancel}
            className="px-3 py-2 rounded-md"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="engineering-form-cancel-btn"
          >
            Cancel
          </button>
        )}
        <button
          type="button" onClick={submit} disabled={busy}
          className="px-3 py-2 rounded-md"
          style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}
          data-testid="engineering-form-submit-btn"
        >
          {busy ? "Saving…" : (progress === 100 ? "Save & complete" : "Save progress")}
        </button>
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <div className="uppercase mb-1.5" style={mono(9, "#9198b0", 600, "0.8px")}>{children}</div>
);

const inputStyle = (fontSize = 12, resize) => ({
  background: "var(--bg-base, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize,
  resize,
});
